'use client'

import * as React from 'react'
import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  // getSortedRowModel,
  // SortingState,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table'
// import { IconDotsVertical } from '@tabler/icons-react'
import { QuestionDetail } from '@/types/question'
import { truncate } from '@/lib/utils'
import { getMarksPercentage, getMarksStyle } from '@/utils/style'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
// import {
//   DropdownMenu,
//   DropdownMenuContent,
//   DropdownMenuItem,
//   DropdownMenuTrigger,
// } from '@/components/ui/dropdown-menu'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import QuestionDrawer from '@/features/question-lib/components/QuestionDrawer'

interface SelectionMetrics {
  selectedCount: number
  totalTime: number
  totalMarks: number
  selectedIds: string[]
  selectedItems: QuestionDetail[]
}

export function DataTableDemo({
  questions: data,
  onSelectionChange,
  enableSelection = true,
  selectedIds,
  onFetchNextPage,
  hasNextPage,
}: {
  questions: QuestionDetail[]
  onSelectionChange?: (metrics: SelectionMetrics) => void
  enableSelection?: boolean
  selectedIds?: string[]
  onFetchNextPage?: () => void
  hasNextPage?: boolean
}) {
  // const [sorting, setSorting] = React.useState<SortingState>([])
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  )
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({})
  const [rowSelection, setRowSelection] = React.useState({})
  const onChangeRef = React.useRef(onSelectionChange)
  React.useEffect(() => {
    onChangeRef.current = onSelectionChange
  }, [onSelectionChange])

  // Prevent duplicate notifications
  const lastNotifyRef = React.useRef<string>('')
  // Track explicit header toggle-all clears so we always notify parent
  const isExplicitToggleAllClearRef = React.useRef<boolean>(false)
  // Track explicit single-row uncheck that may clear the last selection
  const isExplicitRowUncheckRef = React.useRef<boolean>(false)

  const [quesntionId, setQuesionId] = React.useState<number>(0)

  const columns: ColumnDef<QuestionDetail>[] = [
    {
      id: 'select',
      header: ({ table }) => (
        <div className='flex justify-center'>
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && 'indeterminate')
            }
            disabled={(table.options as any).meta?.enableSelection === false}
            onCheckedChange={(value) => {
              if ((table.options as any).meta?.enableSelection === false) return
              // If user explicitly unchecks the header, force a clear notification
              if (!value) isExplicitToggleAllClearRef.current = true
              table.toggleAllPageRowsSelected(!!value)
            }}
            aria-label='Select all'
          />
        </div>
      ),
      cell: ({ row, table }) => (
        <div className='flex justify-center'>
          <Checkbox
            checked={row.getIsSelected()}
            disabled={(table.options as any).meta?.enableSelection === false}
            onCheckedChange={(value) => {
              if ((table.options as any).meta?.enableSelection === false) return
              // If user explicitly unchecks a row, allow clear notification
              if (!value) isExplicitRowUncheckRef.current = true
              row.toggleSelected(!!value)
            }}
            aria-label='Select row'
          />
        </div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'question_id',
      header: () => (
        <div className='text-[14px] font-medium text-[#002652] w-[200px]! min-w-[200px]! max-w-[200px]!'>
          Question Info
        </div>
      ),
      cell: ({ row }) => (
        <div className='capitalize w-[200px]! min-w-[200px]! max-w-[200px]!'>{row.original.question_info}</div>
      ),
    },
    {
      id: 'chapter',
      header: () => (
        <div className='text-[14px] font-medium text-[#002652]'>Chapter</div>
      ),
      cell: ({ row }) => {
        const q = row.original as QuestionDetail

        const chapterText = Array.from(q?.chapters || []).join(', ')
        return <div className='w-[220px]! min-w-[220px]! max-w-[220px]!'>{truncate(chapterText, 30)}</div>
      },
    },
    {
      id: 'question',
      header: () => (
        <div className='text-[14px] font-medium text-[#002652]'>Question Text</div>
      ),
      cell: ({ row }) => {
        const questionText = row.original?.text
        return <div>{truncate(questionText, 30)}</div>
      },
    },
    {
      id: 'subQuestions',
      header: () => (
        <div className='text-center text-[14px] font-medium text-[#002652]'>
          Sub-question
        </div>
      ),
      cell: ({ row }) => {
        return (
          <div className='text-center'>
            {row.original?.sub_questions.length === 0
              ? 'N/A'
              : row.original?.sub_questions.length}
          </div>
        )
      },
    },
    {
      id: 'time',
      header: () => (
        <div className='text-center text-[14px] font-medium text-[#002652]'>
          Time
        </div>
      ),
      cell: ({ row }) => {
        const totalTime = Array.isArray(row.original?.sub_questions)
          ? row.original.sub_questions.reduce(
              (sum, q) => sum + (q?.time_limit || 0),
              0
            )
          : 0
        return <div className='text-center'>{totalTime}</div>
      },
    },
    {
      id: 'marks',
      header: () => (
        <div className='text-center text-[14px] font-medium text-[#002652]'>
          Marks Available
        </div>
      ),
      cell: ({ row }) => {
        const totalMarks = Array.isArray(row.original?.sub_questions)
          ? row.original.sub_questions.reduce(
              (sum, q) => sum + (q?.marks || 0),
              0
            )
          : 0
        return <div className='text-center'>{totalMarks}</div>
      },
    },
    {
      id: 'aiMarks',
      header: () => (
        <div className='text-center text-[14px] font-medium text-[#002652]'>
          Average AI Mark
        </div>
      ),
      cell: ({ row }) => {
        return (
          <div className='text-center'>
            {getMarksPercentage(row.original?.avg_ai_marks)}
          </div>
        )
      },
    },
    {
      id: 'tutorMarks',
      header: () => (
        <div className='text-center text-[14px] font-medium text-[#002652]'>
          Average Tutor Mark
        </div>
      ),
      cell: ({ row }) => {
        return (
          <div className='text-center'>
            {getMarksPercentage(row.original?.avg_tutor_marks)}
          </div>
        )
      },
    },
    {
      id: 'actions',
      enableHiding: false,
      cell: ({ row: _row }) => {
        // const question = row.original;

        return (
          // <DropdownMenu>
          //   <DropdownMenuTrigger asChild>
          //     <Button variant='ghost' className='h-8 w-8 p-0'>
          //       <span className='sr-only'>Open menu</span>
          //       <IconDotsVertical />
          //     </Button>
          //   </DropdownMenuTrigger>
          //   <DropdownMenuContent align='end'>
          //     <DropdownMenuItem
          //       className='cursor-pointer'
          //       onClick={() => {
          //         setQuesionId(_row.original.question_id)
          //       }}
          //     >
          //       View
          //     </DropdownMenuItem>
          //   </DropdownMenuContent>
          // </DropdownMenu>
          <Button variant='ghost' className='p-0 text-[#0057B8] hover:text-[#0057B8]/80 hover:bg-transparent hover:underline' onClick={() => {
            setQuesionId(_row.original.question_id)
          }}>
            View Details
          </Button>
        )
      },
    },
  ]

  const table = useReactTable({
    data,
    columns,
    getRowId: (row) => String((row as QuestionDetail).question_id),
    // onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    // getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: (updater) => {
      if (!enableSelection) return
      // Support both function and value updaters
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const next =
        typeof updater === 'function' ? (updater as any)(rowSelection) : updater
      setRowSelection(next)
    },
    state: {
      // sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
    meta: { enableSelection },
  })

  // Sync external selectedIds to internal rowSelection (for preview mode)
  React.useEffect(() => {
    if (!selectedIds || selectedIds.length === 0) {
      if (!enableSelection) return
      return
    }
    const nextSelection: Record<string, boolean> = {}
    const rowModel = table.getRowModel()
    for (const row of rowModel.rows) {
      const id = String((row.original as QuestionDetail)?.question_id)
      if (selectedIds.includes(id)) nextSelection[row.id] = true
    }
    setRowSelection(nextSelection)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(selectedIds), table])

  React.useEffect(() => {
    if (!onChangeRef.current) return
    const selectedRows = table.getSelectedRowModel().rows
    // Guard: do not emit a clear-selection event caused by data changes
    // If parent still has selectedIds but current view has no selected rows,
    // we skip notifying to preserve external selection state.
    if (
      (!selectedRows || selectedRows.length === 0) &&
      selectedIds &&
      selectedIds.length > 0 &&
      // Allow notification when user explicitly cleared via header or row
      !isExplicitToggleAllClearRef.current &&
      !isExplicitRowUncheckRef.current
    ) {
      return
    }
    if (!selectedRows || selectedRows.length === 0) {
      const notifyKey = JSON.stringify({
        selectedCount: 0,
        totalTime: 0,
        totalMarks: 0,
        selectedIds: [],
        selectedItems: [],
      })

      if (lastNotifyRef.current === notifyKey) return

      lastNotifyRef.current = notifyKey
      onChangeRef.current({
        selectedCount: 0,
        totalTime: 0,
        totalMarks: 0,
        selectedIds: [],
        selectedItems: [],
      })
      // Reset the explicit clear flag after notifying
      isExplicitToggleAllClearRef.current = false
      isExplicitRowUncheckRef.current = false
      return
    }

    let totalTime = 0
    let totalMarks = 0
    const selectedRowIds: string[] = []
    const selectedItems: QuestionDetail[] = []

    for (const row of selectedRows) {
      selectedRowIds.push(String(row.original.question_id))
      selectedItems.push(row.original)
      const subQuestions = row.original?.sub_questions || []
      for (const sq of subQuestions) {
        totalTime += sq?.time_limit || 0
        totalMarks += sq?.marks || 0
      }
    }

    const notifyKey = JSON.stringify({
      selectedCount: selectedRows.length,
      totalTime,
      totalMarks,
      selectedIds: selectedRowIds.sort(),
      selectedItems: selectedItems.map((i) => i.question_id).sort(),
    })

    if (lastNotifyRef.current === notifyKey) return

    lastNotifyRef.current = notifyKey
    onChangeRef.current({
      selectedCount: selectedRows.length,
      totalTime,
      totalMarks,
      selectedIds: selectedRowIds,
      selectedItems,
    })
    // Any non-empty selection means not in an explicit clear state
    if (isExplicitToggleAllClearRef.current) {
      isExplicitToggleAllClearRef.current = false
    }
    if (isExplicitRowUncheckRef.current) {
      isExplicitRowUncheckRef.current = false
    }
  }, [rowSelection, onSelectionChange, table])

  const fetchLockRef = React.useRef(false)

  return (
    <div
      className='h-[300px] overflow-auto'
      onScroll={(e) => {
        const el = e.currentTarget
        if (!hasNextPage || !onFetchNextPage) return
        if (fetchLockRef.current) return
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 120) {
          fetchLockRef.current = true
          onFetchNextPage()
          setTimeout(() => (fetchLockRef.current = false), 500)
        }
      }}
    >
      <Table className='border-b bg-white'>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead
                    key={header.id}
                    className={
                      header.column.id === 'aiMarks' ||
                      header.column.id === 'tutorMarks'
                        ? 'p-0'
                        : undefined
                    }
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                )
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && 'selected'}
              >
                {row.getVisibleCells().map((cell) => {
                  const isAi = cell.column.id === 'aiMarks'
                  const isTutor = cell.column.id === 'tutorMarks'
                  const bgClass = isAi
                    ? getMarksStyle(
                        (cell.row.original as QuestionDetail)?.avg_ai_marks
                      )
                    : isTutor
                      ? getMarksStyle(
                          (cell.row.original as QuestionDetail)?.avg_tutor_marks
                        )
                      : undefined
                  const tdClass =
                    isAi || isTutor ? `p-0 text-center ${bgClass}` : undefined
                  return (
                    <TableCell key={cell.id} className={tdClass}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  )
                })}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className='h-24 text-center'>
                No results.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {!!quesntionId && (
        <QuestionDrawer
          fromChapterTable={true}
          questionId={quesntionId}
          setIsOpen={setQuesionId}
        />
      )}
    </div>
  )
}
